/* eslint-disable */
import React, { ChangeEvent, useState } from 'react';
import { Col, Modal, Row } from 'antd';
import { v4 } from 'uuid';
import { UploadChangeParam, UploadFile } from 'antd/lib/upload/interface';
import { StyledUpload } from './components/StyledUpload';
import { dummyRequest } from './utils/dummyRequest';
import { checkFileIsImage, IMAGE_ACCEPT } from './utils/checkFileIsImage';
import { getBase64 } from './utils/getBase64';
import { UploadButton } from './components/UploadButton';
import { CaptureButton } from './components/CaptureButton';
import { useIntl } from 'react-intl';
import { ImageUpload as messages } from './messages';

export interface ImageUploadProps {
  onImageUpload?: (data: UploadFile<unknown>[]) => void;
  fileList?: UploadFile<unknown>[];
  uploadText?: string;
  captureText?: string;
  name?: string;
  /**
   * Whether to support selected multiple files. IE10+ supported.
   * You can select multiple files with CTRL holding down while multiple is set to be true.
   * @see https://ant.design/components/upload#api
   * @default false
   */
  multiple?: boolean;
}

export const ImageUpload: React.FC<ImageUploadProps> = ({
  onImageUpload,
  fileList,
  uploadText,
  captureText,
  name,
  multiple,
}) => {
  const intl = useIntl();
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewImage, setPreviewImage] = useState<string | ArrayBuffer | null>('');

  const handleCancel = () => setPreviewVisible(false);

  const handlePreview = async (file: UploadFile) => {
    if (file.originFileObj) {
      setPreviewImage(await getBase64(file.originFileObj));
      setPreviewVisible(true);
    }
  };

  const handleUpload = (data: Pick<UploadChangeParam<UploadFile<unknown>>, 'fileList'>) => {
    onImageUpload?.(data.fileList);
    return true;
  };

  const handleCapture = (event: ChangeEvent<HTMLInputElement>) => {
    const [newNativeFile] = Array.from(event.target.files ?? []);

    if (!newNativeFile || checkFileIsImage(newNativeFile) !== true) {
      return;
    }

    const uuid = v4();

    const newFile: UploadFile<unknown> = {
      uid: uuid,
      name: newNativeFile.name,
      // @ts-expect-error assigning File to RcFile
      originFileObj: newNativeFile
    };

    handleUpload({
      fileList: [
        ...(fileList ?? []),
        newFile,
      ],
    });
  };

  return (
    <>
      <StyledUpload
        accept={IMAGE_ACCEPT}
        beforeUpload={checkFileIsImage}
        // @ts-expect-error customRequest is required to prevent actual upload
        customRequest={dummyRequest}
        fileList={fileList}
        listType="picture-card"
        multiple={multiple}
        name={name}
        onChange={handleUpload}
        onPreview={handlePreview}
      >
        {fileList && fileList.length >= 30 ? null : (
          <Row justify="space-around" gutter={25} >
            <Col flex={1}>
              <CaptureButton text={captureText ?? intl.formatMessage(messages.capture)} onCapture={handleCapture} />
            </Col>
            <Col flex={1}>
              <UploadButton text={uploadText ?? intl.formatMessage(messages.upload)} />
            </Col>
          </Row>
        )}
      </StyledUpload>
      <Modal footer={null} title={null} open={previewVisible} onCancel={handleCancel}>
        <img alt="preview" className="w-100" src={typeof previewImage === 'string' ? previewImage : ''} />
      </Modal>
    </>
  );
};
